"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CapturePhase, LightingQuality } from "@/features/assessments/components/ImageCaptureStep/captureTypes";
import { useSharedFaceLandmarker } from "@/features/facialRecognition/FaceLandmarkerProvider";
import { classifyLightingQuality, sampleVideoBrightness } from "@/features/facialRecognition/brightnessAnalysis";
import { MEDIAPIPE_STUCK_TIMEOUT_MS } from "@/features/facialRecognition/mediapipeConfig";
import { type FaceStatus, getGuidanceMessage } from "@/features/facialRecognition/useFaceLandmarker";
export type MlFailureReason = "timeout" | "error" | "runtime_error";
const PERFECT_FRAMES_REQUIRED = 15;
const READY_HOLD_TIME = 1500;
const READY_DROP_DEBOUNCE = 300;
const ML_LOAD_TIMEOUT_MS = 10000;

/**
 * `onMlDetectionUnavailable` and `onStuckTimeoutFired` are optional
 * observability hooks. The hook itself does not log — it stays free of
 * assessment/i18n concerns so it can be reused outside the assessment
 * flow. Consumers that run inside an assessment should wire these to
 * `assessmentLogger.warn(...)` so Datadog gets full
 * `{ assessmentId, subdomainSlug, flowType, ... }` correlation for the
 * specific session. Each callback is invoked at most once per guidance
 * session (the internal `hasFiredMlFallbackRef` guard still applies).
 */
type UseFaceGuidanceOptions = {
  phase: CapturePhase;
  countdown: number | null;
  setCountdown: (value: number | null) => boolean;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  setPhase: (phase: CapturePhase) => void;
  onMlDetectionUnavailable?: (reason: MlFailureReason, phase: CapturePhase) => void;
  onStuckTimeoutFired?: (lastStatus: FaceStatus, phase: CapturePhase) => void;
};
export function useFaceGuidance({
  phase,
  countdown,
  setCountdown,
  videoRef,
  setPhase,
  onMlDetectionUnavailable,
  onStuckTimeoutFired
}: UseFaceGuidanceOptions) {
  const setCountdownRef = useRef(setCountdown);
  const onMlDetectionUnavailableRef = useRef(onMlDetectionUnavailable);
  const onStuckTimeoutFiredRef = useRef(onStuckTimeoutFired);
  onMlDetectionUnavailableRef.current = onMlDetectionUnavailable;
  onStuckTimeoutFiredRef.current = onStuckTimeoutFired;
  const phaseRef = useRef(phase);
  const faceStatusRef = useRef<FaceStatus>("initializing");
  // useLayoutEffect runs synchronously after commit and before paint, closing
  // the race window that useEffect (post-paint) leaves open for pending timers.
  useLayoutEffect(() => {
    setCountdownRef.current = setCountdown;
    phaseRef.current = phase;
  });
  const [faceStatus, setFaceStatus] = useState<FaceStatus>("initializing");
  const [lightingQuality, setLightingQuality] = useState<LightingQuality>("good");
  const [canManualCapture, setCanManualCapture] = useState(false);
  const [lightingAnimationPhase, setLightingAnimationPhase] = useState<"pulsing" | "settled">("pulsing");
  const [mlLoadingTimedOut, setMlLoadingTimedOut] = useState(false);
  const hasFiredMlFallbackRef = useRef(false);
  const brightnessCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const brightnessRef = useRef<number | null>(null);
  const lastLandmarksRef = useRef<Array<{
    x: number;
    y: number;
    z: number;
  }> | null>(null);
  const perfectFrameCountRef = useRef(0);
  const readyTimerRef = useRef<NodeJS.Timeout | null>(null);
  const readyDropTimerRef = useRef<NodeJS.Timeout | null>(null);
  const {
    isInitialized: isMLReady,
    error: mlError,
    runtimeError: mlRuntimeError,
    startDetection,
    stopDetection,
    detectFaceInImage
  } = useSharedFaceLandmarker();

  // A runtime failure inside MediaPipe (procrustes_solver / CalculatorGraph)
  // leaves the graph in a state where every subsequent detect call also
  // throws. Treat it the same as an init error: drop into manual-capture
  // immediately rather than stalling the user on the 10s stuck-timer fallback.
  const effectiveMlError = mlError ?? mlRuntimeError;
  const enableManualCaptureDueToMlFailure = useCallback((reason: MlFailureReason) => {
    setMlLoadingTimedOut(true);
    setCanManualCapture(true);
    if (hasFiredMlFallbackRef.current) return;
    hasFiredMlFallbackRef.current = true;
    onMlDetectionUnavailableRef.current?.(reason, phaseRef.current);
  }, [setCanManualCapture]);
  const analyzeBrightness = useCallback((video: HTMLVideoElement) => {
    if (!brightnessCanvasRef.current) {
      brightnessCanvasRef.current = document.createElement("canvas");
      brightnessCanvasRef.current.width = 64;
      brightnessCanvasRef.current.height = 64;
    }
    const avgBrightness = sampleVideoBrightness(video, brightnessCanvasRef.current);
    if (avgBrightness === null) return;
    brightnessRef.current = avgBrightness;
    setLightingQuality(classifyLightingQuality(avgBrightness));
  }, []);
  useEffect(() => {
    if (phase === "positioning" && isMLReady && videoRef.current) {
      startDetection(videoRef.current, result => {
        faceStatusRef.current = result.status;
        setFaceStatus(result.status);
        if (result.landmarks && result.landmarks.length > 0) {
          const landmarks = result.landmarks[0].map(({
            x,
            y,
            z
          }) => ({
            x,
            y,
            z
          }));
          lastLandmarksRef.current = landmarks;
        }
        if (result.faceCount > 0 && videoRef.current) {
          analyzeBrightness(videoRef.current);
        }
        if (result.status === "perfect") {
          perfectFrameCountRef.current++;
          if (perfectFrameCountRef.current >= 5) {
            setCanManualCapture(true);
          }
          const currentBrightness = brightnessRef.current;
          const isLightingAcceptable = currentBrightness !== null && currentBrightness >= 0.3 && currentBrightness < 0.8;
          if (perfectFrameCountRef.current >= PERFECT_FRAMES_REQUIRED && isLightingAcceptable) {
            setPhase("ready");
          } else if (perfectFrameCountRef.current >= PERFECT_FRAMES_REQUIRED && !isLightingAcceptable) {
            setCanManualCapture(true);
          }
        } else {
          perfectFrameCountRef.current = 0;
        }
      });
    }
    return () => {
      stopDetection();
    };
  }, [phase, isMLReady, startDetection, stopDetection, analyzeBrightness, setPhase, videoRef]);
  useEffect(() => {
    const shouldWatchForMlTimeout = (phase === "positioning" || phase === "initializing-ml") && !isMLReady && !effectiveMlError;
    if (shouldWatchForMlTimeout && !mlLoadingTimedOut) {
      const timeout = setTimeout(() => {
        enableManualCaptureDueToMlFailure("timeout");
        if (phaseRef.current === "initializing-ml") {
          setPhase("positioning");
        }
      }, ML_LOAD_TIMEOUT_MS);
      return () => clearTimeout(timeout);
    }
    return;
  }, [phase, isMLReady, effectiveMlError, mlLoadingTimedOut, enableManualCaptureDueToMlFailure, setPhase]);
  useEffect(() => {
    if (mlError) {
      const timeout_0 = setTimeout(() => {
        enableManualCaptureDueToMlFailure("error");
      }, 0);
      return () => clearTimeout(timeout_0);
    }
    return;
  }, [mlError, enableManualCaptureDueToMlFailure]);
  useEffect(() => {
    if (!mlRuntimeError) return;
    enableManualCaptureDueToMlFailure("runtime_error");
    if (phaseRef.current === "initializing-ml") {
      setPhase("positioning");
    }
  }, [mlRuntimeError, enableManualCaptureDueToMlFailure, setPhase]);
  useEffect(() => {
    if (phase !== "positioning" || !isMLReady || canManualCapture) return;
    const timeout_1 = setTimeout(() => {
      if (!hasFiredMlFallbackRef.current) {
        hasFiredMlFallbackRef.current = true;
        onStuckTimeoutFiredRef.current?.(faceStatusRef.current, phaseRef.current);
      }
      setCanManualCapture(true);
    }, MEDIAPIPE_STUCK_TIMEOUT_MS);
    return () => clearTimeout(timeout_1);
  }, [phase, isMLReady, canManualCapture]);
  useEffect(() => {
    if (isMLReady) {
      const timeout_2 = setTimeout(() => {
        setMlLoadingTimedOut(false);
      }, 0);
      return () => clearTimeout(timeout_2);
    }
    return;
  }, [isMLReady]);
  useEffect(() => {
    if (phase === "ready") {
      readyTimerRef.current = setTimeout(() => {
        // Guard against race: manual capture may have already changed phase
        if (phaseRef.current !== "ready") return;
        stopDetection();
        const didStartCountdown = setCountdownRef.current(3);
        if (!didStartCountdown) return;
        setPhase("capturing");
      }, READY_HOLD_TIME);
      return () => {
        if (readyTimerRef.current) {
          clearTimeout(readyTimerRef.current);
        }
      };
    }
  }, [phase, setPhase, stopDetection]);
  useEffect(() => {
    if (phase === "ready" && countdown === null) {
      if (faceStatus === "perfect") {
        if (readyDropTimerRef.current) {
          clearTimeout(readyDropTimerRef.current);
          readyDropTimerRef.current = null;
        }
      } else if (!readyDropTimerRef.current) {
        readyDropTimerRef.current = setTimeout(() => {
          if (readyTimerRef.current) {
            clearTimeout(readyTimerRef.current);
          }
          perfectFrameCountRef.current = 0;
          setPhase("positioning");
          readyDropTimerRef.current = null;
        }, READY_DROP_DEBOUNCE);
      }
    }
    return () => {
      if (readyDropTimerRef.current) {
        clearTimeout(readyDropTimerRef.current);
        readyDropTimerRef.current = null;
      }
    };
  }, [phase, faceStatus, countdown, setPhase]);
  useEffect(() => {
    if (phase === "positioning" || phase === "ready") {
      const pulseTimer = setTimeout(() => {
        setLightingAnimationPhase("pulsing");
      }, 0);
      const settleTimer = setTimeout(() => {
        setLightingAnimationPhase("settled");
      }, 2400);
      return () => {
        clearTimeout(pulseTimer);
        clearTimeout(settleTimer);
      };
    }
  }, [phase]);
  useEffect(() => {
    if (phase === "initializing-ml" && isMLReady) {
      setPhase("positioning");
    } else if (phase === "initializing-ml" && effectiveMlError) {
      setPhase("positioning");
    }
  }, [phase, isMLReady, effectiveMlError, setPhase]);
  const getGuidanceText = () => {
    if (phase === "consent") return "";
    if (phase === "requesting") return "Allow camera access";
    if (phase === "initializing-ml") return "";
    if (phase === "captured") return "Perfect!";
    if (phase === "capturing") return "";
    if (phase === "analyzing") return "Analyzing facial landmarks...";
    if (phase === "ready") return "Perfect! Hold still...";
    if (effectiveMlError || mlLoadingTimedOut) return "";
    if (faceStatus === "initializing") return "";
    const guidance = getGuidanceMessage(faceStatus);
    return guidance.text;
  };
  const getGuidanceSubtext = () => {
    if (phase === "ready") return "Get ready for capture";
    if (phase === "initializing-ml") return "";
    if (effectiveMlError || mlLoadingTimedOut) return "";
    if (faceStatus === "initializing") return "";
    const guidance_0 = getGuidanceMessage(faceStatus);
    return guidance_0.subtext;
  };
  return {
    isMLReady,
    detectFaceInImage,
    stopDetection,
    faceStatus,
    lightingQuality,
    lightingAnimationPhase,
    canManualCapture,
    setCanManualCapture,
    mlError: effectiveMlError,
    mlLoadingTimedOut,
    lastLandmarksRef,
    getGuidanceText,
    getGuidanceSubtext
  };
}